import React from 'react';
import { useNavigate } from 'react-router-dom';
import StakeholderStat from '@/components/brand/StakeholderStat';

const STAKE_ASSET = '/assets/farmer-home';

/**
 * Shared shells used by every role's refreshed home so all four
 * dashboards read as the same product. Farmer already ships an inline
 * copy of some of these — kept in place to avoid a farmer-side
 * regression in this PR; a follow-up will DRY them out.
 */

// `className` sets the edge spacing: full-bleed by default; framed
// sections (MarketplaceShowcase) pass their own.
export function HomeMarquee({ dataTestId, seconds = 45, isStatic = false, className = '-mx-4 px-4 mb-5', children }) {
  // Static mode (fewer than 4 real items) renders a plain horizontal
  // row — no CSS animation, no viewport mask — so a single product
  // doesn't visibly repeat across the strip. The outer margins stay
  // the same so the surrounding layout doesn't shift.
  if (isStatic) {
    return (
      <div className={className} data-testid={dataTestId}>
        <div className="flex gap-3 overflow-x-auto no-scrollbar">{children}</div>
      </div>
    );
  }
  return (
    <div className={`farm-marquee-viewport ${className}`} data-testid={dataTestId}>
      <div
        className="farm-marquee-track"
        style={{ '--marquee-duration': `${seconds}s` }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Build the item list for a scrolling marquee.
 *
 * The returned `{ rows, isPadded, isStatic }` is the shape callers
 * (HomeMarquee + the inline carousels in FarmerHome) consume.
 *
 * With >= `staticThreshold` real items (default 4) the list wraps
 * around to at least `min` entries and is then duplicated once so the
 * CSS `-50%` slide keyframe loops seamlessly. `isPadded(i)` is true
 * for the ghost copies so callers can mark them aria-hidden and drop
 * them from the tab order.
 *
 * With fewer real items the previous version padded to 6 and still
 * duplicated — so a lone product rendered twelve times across the
 * strip, which looked like a seeding bug. Below the threshold we
 * now return the real items once (no padding, no duplication) and
 * flag `isStatic=true`; the caller renders a plain row and skips the
 * marquee animation entirely.
 */
export function useMarqueeItems(items, min = 6, staticThreshold = 4) {
  const source = items || [];
  const realCount = source.length;

  if (realCount === 0) {
    return { rows: [], isPadded: () => false, isStatic: true };
  }

  if (realCount < staticThreshold) {
    // Static mode — just render what we have, once, no repeat.
    return { rows: [...source], isPadded: () => false, isStatic: true };
  }

  const padded = [];
  const target = Math.max(realCount, min);
  for (let i = 0; i < target; i++) padded.push(source[i % realCount]);
  const rows = [...padded, ...padded];
  const isPadded = (index) => index >= realCount;
  return { rows, isPadded, isStatic: false };
}

export function HomeSectionTitle({ children, className = '', trailing }) {
  return (
    <div className={`flex items-baseline justify-between mb-2 ${className}`}>
      <h2 className="font-heading text-lg font-bold text-farm-gold">{children}</h2>
      {trailing}
    </div>
  );
}

// The home search bar, with its typeahead, lives in HomeSearch.js.
export { default as HomeSearchBar } from './HomeSearch';

// A tile with `onClick` is a button (the farmer's statistics open My Farm).
export function HomeStatsCard({ tiles, dataTestId = 'home-statistics' }) {
  return (
    <div className="grid grid-cols-3 gap-2 bg-white rounded-2xl p-3 mb-5" data-testid={dataTestId}>
      {tiles.map((tile) => {
        const body = (
          <>
            <div className={`w-8 h-8 rounded-lg grid place-items-center ${tile.iconClass} mb-1.5`}>
              <tile.icon className="w-4 h-4" />
            </div>
            <p className="font-heading font-bold text-xl text-farm-forest leading-none">{tile.value}</p>
            <p className="text-[11px] text-farm-forest/60 mt-1">{tile.label}</p>
          </>
        );
        const cls = 'flex flex-col items-center text-center';
        return tile.onClick ? (
          <button
            key={tile.label}
            type="button"
            onClick={tile.onClick}
            className={`${cls} focus:outline-none focus:ring-2 focus:ring-farm-primary rounded-lg`}
          >
            {body}
          </button>
        ) : (
          <div key={tile.label} className={cls}>{body}</div>
        );
      })}
    </div>
  );
}

// The four photo circles. Each opens the directory of that user type.
// Farmer first, then up the supply chain.
const STAKEHOLDERS = [
  { type: 'farmer', image: 'stakeholder-farmer.jpg', total: 'total_farmers', label: 'farmerHome.stakeholders.farmers' },
  { type: 'shopkeeper', image: 'stakeholder-shopkeeper.jpg', total: 'total_shopkeepers', label: 'farmerHome.stakeholders.shopkeepers' },
  { type: 'distributor', image: 'stakeholder-distributor.jpg', total: 'total_distributors', label: 'farmerHome.stakeholders.distributors' },
  { type: 'manufacturer', image: 'stakeholder-manufacturer.jpg', total: 'total_manufacturers', label: 'farmerHome.stakeholders.manufacturers' },
];

export function HomeStakeholderStrip({ platform, t, className = 'gap-1' }) {
  const navigate = useNavigate();
  return (
    <div className={`grid grid-cols-4 mb-4 ${className}`} data-testid="home-stakeholders">
      {STAKEHOLDERS.map((s) => (
        <StakeholderStat
          key={s.type}
          image={`${STAKE_ASSET}/${s.image}`}
          count={platform[s.total] || 0}
          label={t(s.label)}
          onClick={() => navigate(`/directory/${s.type}`)}
          dataTestId={`stakeholder-${s.type}`}
        />
      ))}
    </div>
  );
}

// 0 means no price set (base_price defaults to 0), so show nothing.
export function priceNGN(v) {
  return typeof v === 'number' && v > 0 ? `₦${v.toLocaleString()}` : '';
}
